import { CheckCircle, CircleOutlined, HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid, Typography } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInAirbagTellTalePhotoDto, vehicleCheckInAirbagTellTalePhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

const AIRBAG_TELL_TALE_DEFAULT_TYPE = 'Passenger Air Bag On/Off Text with Tell-Tale'

interface AirbagTellTalePhotoDialogProps {
  initialValues: VehicleCheckInAirbagTellTalePhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInAirbagTellTalePhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function AirbagTellTalePhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: AirbagTellTalePhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik<VehicleCheckInAirbagTellTalePhotoDto>
        enableReinitialize={true}
        initialValues={{
          ...initialValues,
          vehicleCheckInAirbagTellTalePhotoType:
            initialValues.vehicleCheckInAirbagTellTalePhotoType != null &&
            initialValues.vehicleCheckInAirbagTellTalePhotoType !== ''
              ? initialValues.vehicleCheckInAirbagTellTalePhotoType
              : AIRBAG_TELL_TALE_DEFAULT_TYPE
        }}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInAirbagTellTalePhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Airbag Tell-Tale Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Airbag Tell-Tale'
                      file={values.vehicleCheckInAirbagTellTalePhotoFile}
                      placeholderSrc='/10 Airbag tell-tale.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='airbag-telltale-browse'
                    cameraInputId='airbag-telltale-camera'
                    disabled={isSubmitting || isLoading}
                    onFileChosen={file => setFieldValue('vehicleCheckInAirbagTellTalePhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.vehicleCheckInAirbagTellTalePhotoType)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Airbag Tell-Tale Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography>
                      Please select the type of image you are taking by pressing a button below. If vehicle does not have Airbag
                      Tell-Tale, please take a photo of the Passenger Visor Label.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={
                        values.vehicleCheckInAirbagTellTalePhotoType === 'Passenger Air Bag On/Off Text with Tell-Tale' ? (
                          <CheckCircle />
                        ) : (
                          <CircleOutlined />
                        )
                      }
                      fullWidth
                      onClick={() => {
                        setFieldValue('vehicleCheckInAirbagTellTalePhotoType', 'Passenger Air Bag On/Off Text with Tell-Tale')
                      }}
                      variant={
                        values.vehicleCheckInAirbagTellTalePhotoType === 'Passenger Air Bag On/Off Text with Tell-Tale'
                          ? 'contained'
                          : 'outlined'
                      }
                    >
                      PASSENGER AIR BAG ON/OFF TEXT WITH TELL-TALE
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={true}
                      endIcon={
                        values.vehicleCheckInAirbagTellTalePhotoType === 'Tell-Tale Only (Symbol Only)' ? (
                          <CheckCircle />
                        ) : (
                          <CircleOutlined />
                        )
                      }
                      fullWidth
                      variant={
                        values.vehicleCheckInAirbagTellTalePhotoType === 'Tell-Tale Only (Symbol Only)'
                          ? 'contained'
                          : 'outlined'
                      }
                    >
                      TELL-TALE ONLY (SYMBOL ONLY)
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={
                        values.vehicleCheckInAirbagTellTalePhotoType === 'Passenger Visor Label / No Airbag Tell-Tale' ? (
                          <CheckCircle />
                        ) : (
                          <CircleOutlined />
                        )
                      }
                      fullWidth
                      onClick={() => {
                        setFieldValue('vehicleCheckInAirbagTellTalePhotoType', 'Passenger Visor Label / No Airbag Tell-Tale')
                      }}
                      variant={
                        values.vehicleCheckInAirbagTellTalePhotoType === 'Passenger Visor Label / No Airbag Tell-Tale'
                          ? 'contained'
                          : 'outlined'
                      }
                    >
                      PASSENGER VISOR LABEL / NO AIRBAG TELL-TALE
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || The default selection when type was not previously saved is “Passenger Air Bag On/Off Text with
                      Tell-Tale”. || “Tell-Tale Only (Symbol Only)” is disabled. || Pressing “Previous” goes to the previous
                      photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
